import {useRef, useEffect, useState, useMemo, useCallback} from 'react';
import Tooltip from '@mui/material/Tooltip';
import Map from "@arcgis/core/Map";
import MapView from '@arcgis/core/views/MapView';
import Point from '@arcgis/core/geometry/Point';

import ToolBar from "./features/map-components/toolbar/Toolbar";
import { useStoreContext } from './store/context';
import { ONEHR, ONEMIN, ONESEC, combineStationObservationResponses, errorResponse } from './store/common';
import { 
  useCurrentDate, 
  useCurrentDateSlider, 
  useDates, 
  useMidDateSelected, 
  useCurrentZoom, 
  useAddAlert, 
  useBaseMapSelection, 
  useGT05Displayed, 
  useGT50Displayed, 
  useSF100Displayed, 
  useNOAA100Displayed,
} from './store/actions';
import moment from 'moment-timezone';
import { formatDataToLayer } from './features/map-components/layers/WeatherLayers/WeatherElementLayer';
import { alertMessages,chartTableList, chartTableListDaily, ndrsChartDailyList, ndrsChartHourlyList } from './constants';
import { checkBannerOutage, getStationsMap,} from './apis/getWeatherCalls';
import  BaseMapToggleButton, {imageryUSGSBasemap} from './features/map-components/baseMap/BaseMapToggleButton';
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import Graphic from '@arcgis/core/Graphic';
import { useHistory, useLocation } from 'react-router';
import './features/map-components/popup/Popup.scss'
import CustomLegend from './features/map-components/legend/CustomLegend';
import SearchWeatherStations from './features/map-components/search/stationSearch/SearchWeatherStations';
import { formatWindArrowLayer } from './features/map-components/layers/WeatherLayers/WeatherWindArrowLayer';
import { formatWindBarbLayer } from './features/map-components/layers/WeatherLayers/WeatherBarbLayer';
import FeatureFilter from "@arcgis/core/layers/support/FeatureFilter";
import PopupWxNfdr from './features/map-components/popup/PopupWxNfdr';
import SearchSite from './features/map-components/search/siteSearch/SearchSite';
import { getSitesMap } from './apis/getFuelSampleCalls';
import { formatFSDataToLayer } from './features/map-components/layers/FieldSampleLayers/FieldSampleLayer';
import PopUpFieldSample from './features/map-components/popup/PopUpFieldSample';
import { formatNfdrDataToLayer } from './features/map-components/layers/NFDRLayers/NfdrLayer';

const EsriMap: React.FC = () => {
  /*variables to add esri map*/
  const mapDiv:any = useRef();
  const map:any = useRef();
  const mapView:any = useRef();
  const [state, dispatch] = useStoreContext();

  /*variables for the 30 min "display time" updates*/
  const [timeHit30, setTimeHit30]=useState<boolean>(false);
  const [todaysHour, setTodaysHour] = useState(new Date().getHours());
  const [todaysDay, setTodaysDay] = useState(new Date().getDate());
  const [todaysMonth, setTodaysMonth] = useState(new Date().getMonth());
  const [todaysMin, setTodaysMin] = useState(new Date().getMinutes());
  const setDates = useDates(dispatch);
  const setMiddleDate = useMidDateSelected(dispatch);
  const setCurrentSliderDate = useCurrentDateSlider(dispatch);
  const setCurrentDay = useCurrentDate(dispatch);

  const addAlert = useAddAlert(dispatch);
  const location = useLocation(); 
  const setBaseMap = useBaseMapSelection(dispatch);
  /* retry calls if needed*/
  const [nfdrsErrorsCount, setNfdrsErrorsCount] = useState<number>(0);
  const [wxErrorCount, setWxErrorCount] = useState<number>(0);

  /* variables for pop up and hover*/
  const [selectedStation, setSelectedStation] = useState<any>(null);
  const [popupPosition, setPopupPosition] = useState(localStorage.getItem('popup')!==null? JSON.parse(localStorage.getItem('popup')??''):{x:500, y: 100});
  const [tooltipContent, setTooltipContent] = useState<string>('');
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  
  const [selectedSite, setSelectedSite] = useState<any>(null);

  const [responses, setResponses] = useState<any>(null);

  /* variables for data outage banner*/
  const setGT05Displayed = useGT05Displayed(dispatch);
  const setGT50Displayed = useGT50Displayed(dispatch);
  const setSF100Displayed = useSF100Displayed(dispatch);
  const setNOAA100Displayed = useNOAA100Displayed(dispatch);
  const [calledDataOutage, setCalledDataOutage] = useState(false);
  const history = useHistory();

  let screenHeight = window.innerHeight;

  useEffect(()=>{
    if(location.pathname.includes('/inap')){
      const timer = setTimeout(()=> {
        history.push({pathname:`/ui`, key: 'from-inap'})
      }, 2000);
      return () => clearTimeout(timer);
    }
  },[history, location.pathname])

  /*weather wind speed call back - will add/remove weather wind speed barbs onto map*/
  const weatherWindSpeedCallback = useCallback((request:any)=>{
    if(state.wind_speed && (state.mapDataType==='Weather')){
      const barbLayer = formatWindBarbLayer(request.response, request.zoomLevel, request.startDate, request.endDate, request.radio);
      const windSpeedIDNotToRemove = barbLayer.id
      map.current.add(barbLayer, 0)
      let matchingWindSpeed: any[] =[];
      let layerToRemove : any[]=[];
      map.current.layers.forEach((layer) => {
        if(layer.id.includes('windSpeedBarbs') && layer.id !== windSpeedIDNotToRemove){
            layerToRemove.push(layer)
        }
        if(layer.id === windSpeedIDNotToRemove){
          matchingWindSpeed.push(layer)
        }
      });
      if (layerToRemove.length >= 1) {
        map.current.removeMany(layerToRemove)
      }
      if (matchingWindSpeed.length > 1) {
        matchingWindSpeed.pop();
        map.current.removeMany(matchingWindSpeed)
      }
    }
    else{
      let layerToRemove : any[]=[];
      map.current.layers.forEach((layer) => {
        if(layer.id.includes('windSpeedBarbs')){
            layerToRemove.push(layer)
        }
      });
      if (layerToRemove.length >= 1) {
        map.current.removeMany(layerToRemove)
      }
    }
  },[state.wind_speed, state.mapDataType]);
  
  /* weather direction call back- will add/remove weather wind speed data onto map*/
  const weatherDirCallBack = useCallback((request:any)=>{
    if(state.wind_direction && (state.mapDataType==='Weather')){
      const arrowLayer = formatWindArrowLayer(request.response, request.zoomLevel, request.startDate, request.endDate, request.radio);
      const windDirectionIDNotToRemove = arrowLayer.id
      map.current.add(arrowLayer, 0)
      let matchingWindDir: any[] =[];
      let layerToRemove : any[]=[];
      map.current.layers.forEach((layer) => {
        if(layer.id.includes('windDirectionArrows') && layer.id !== windDirectionIDNotToRemove){
            layerToRemove.push(layer)
        }
        if(layer.id === windDirectionIDNotToRemove){
            matchingWindDir.push(layer)
        }
      });
      if (layerToRemove.length >= 1) {
        map.current.removeMany(layerToRemove)
      }
      if (matchingWindDir.length > 1) {
        matchingWindDir.pop();
        map.current.removeMany(matchingWindDir)
      }

    }
    else{
      let layerToRemove : any[]=[];
      map.current.layers.forEach((layer) => {
        if(layer.id.includes('windDirectionArrows')){
            layerToRemove.push(layer)
        }
      });
      if (layerToRemove.length >= 1) {
        map.current.removeMany(layerToRemove)
      }
    }
  },[state.wind_direction, state.mapDataType]);

  /* hourly weather call and add it onto the map. It will also remove old weather Obs layers*/
  const weatherCallback = useCallback((request:any, counter: number)=>{
    (async()=>{
        try{
            let response: any;
            let geoLayers:any;

            const varRequest = {
              ...request,
              returnAll: true,
              weatherDailyVisible:'TRUE',
            };

            const varRequestHourly = {
              ...request,
              weatherDailyVisible: 'ALL',
              nfdrsDailyVisible:'ALL', 
              weatherHourlyVisible: 'TRUE', 
              nfdrsHourlyVisible: 'ALL', 
              weatherDailyDownload: 'ALL', 
              weatherHourlyDownload: 'ALL', 
              nfdrsDailyDownload: 'ALL', 
              nfdrsHourlyDownload: 'ALL', 
              produceNfdrs: 'ALL', 
              ingestObservation: 'ALL', 
              returnAll: true
            };
            if(state.selectedRadio.type==='hourly'){
              const responseStations: any = await getStationsMap(varRequestHourly, 'hourlyWx', state.user);
              response = combineStationObservationResponses(responseStations?.stations, responseStations?.obs, 'hourlyWeather',request.endDate);
              geoLayers = formatDataToLayer(response, request.element,request.zoomLevel, request.startDate, request.endDate, request.radio);
              setWxErrorCount(0);
              if(responseStations?.stations?.length<1){
                setWxErrorCount(counter+1)
              }
            }
            if(state.selectedRadio.type==='daily'){
              const responseStations: any = await getStationsMap(varRequest, 'dailyWx', state.user);
              setWxErrorCount(0);
              response = combineStationObservationResponses(responseStations?.stations, responseStations?.obs, 'dailyWeather', request.endDate);
              geoLayers = formatDataToLayer(response, request.element,request.zoomLevel, request.startDate, request.endDate, request.radio);
              if(responseStations?.stations?.length<1 ){
                setWxErrorCount(counter+1)
              }
            }

            setResponses(response);

            const currObsLayer:any = geoLayers?.obsLayer;
            const currForeLayer:any = geoLayers?.forecastLayer;
            const obsIDNotToRemove = currObsLayer.id;
            const forecastIDNotToRemove = currForeLayer.id;

            let layerToRemove : any[]=[];
            let matchingLayersObs: any[]=[];
            let matchingLayerForecast: any[]=[];
            map.current.addMany([currObsLayer, currForeLayer]);

            map.current.layers.forEach((layer) => {
                if (layer.id.includes('observation') && layer.id !== obsIDNotToRemove) {
                    layerToRemove.push(layer)
                }
                if (layer.id.includes('forecastValues') && layer.id !== forecastIDNotToRemove) {
                    layerToRemove.push(layer)
                }
                if(layer.id.includes('fieldSample') || layer.id.includes('NFDR')){
                    layerToRemove.push(layer)
                }
                if (layer.id === obsIDNotToRemove) {
                    matchingLayersObs.push(layer)
                }
                if (layer.id === forecastIDNotToRemove) {
                    matchingLayerForecast.push(layer)
                }
            });
            if (layerToRemove.length >= 1) {
                map.current.removeMany(layerToRemove)
            }
            if (matchingLayerForecast.length > 1) {
                matchingLayerForecast.pop();
                map.current.removeMany(matchingLayerForecast)
            }
            if (matchingLayersObs.length > 1) {
                matchingLayersObs.pop();
                map.current.removeMany(matchingLayersObs)
            }

            const param = {
              ...request,
              response: response
            }
            weatherDirCallBack(param);
            weatherWindSpeedCallback(param)

            return()=>{
              if(mapView.current){
                mapView.current.destroy()
              }
            }
            
        }
        catch(error){
            errorResponse(error, addAlert, alertMessages.retrievingStationData)
        }
    })();
  },[addAlert, map, weatherDirCallBack, weatherWindSpeedCallback, state.selectedRadio.type, state?.user]);

  /*call to get field sample data and populate fieldSample layers*/
  const fieldSampleCallBack = useCallback((request: any) => {
    (async () => {
      try {
        let response: any;
        let geoLayers:any;
        response = await getSitesMap(null,request);
        geoLayers = formatFSDataToLayer(response, request.fuelSampleMetadataInput.fuelType, request.zoomLevel, request.fuelSampleMetadataInput.startDate, request.fuelSampleMetadataInput.endDate, request.fuelSampleMetadataInput.fuelMethod, request.fuelSampleMetadataInput.fuelCategory, request.fuelSampleMetadataInput.fuelSubCategory)
        setResponses(response);

        const currFSLayer = geoLayers?.dataLayer;

        const fsIDNotToRemove = currFSLayer.id;

        let layerToRemove: any[] = [];
        let matchingLayerFS: any[] =[];
        map.current.addMany([currFSLayer]);

        map.current.layers.forEach((layer) => {
          if (layer.id.includes('observation')|| layer.id.includes('forecastValues') || layer.id.includes('windDirectionArrows')|| layer.id.includes('windSpeedBarbs') || layer.id.includes('NFDR')) {
            layerToRemove.push(layer)
          }
          if(layer.id.includes('fieldSample&FuelType') && layer.id!== fsIDNotToRemove){
            layerToRemove.push(layer)
          }
          if(layer.id === fsIDNotToRemove){
            matchingLayerFS.push(layer)
          }
        });

        if (layerToRemove.length >= 1) {
          map.current.removeMany(layerToRemove)
        }
        if (matchingLayerFS.length > 1) {
          matchingLayerFS.pop();
          map.current.removeMany(matchingLayerFS)
      }


        return () => {
          if (mapView.current) {
            mapView.current.destroy()
          }
        }

      }
      catch (error) {
        errorResponse(error, addAlert, alertMessages.fieldSampleHomeData)
      }
    })();
  }, [addAlert, map,]);

  /* call for nfdrHourly Data and populate layers */
  const nfdrCallback = useCallback((request: any, counter: number) => {
    (async()=>{
      try{
          let response: any;
          let geoLayers:any;
          const varRequest = {
            ...request,
            hasHistoricData: 'ALL',
            returnAll: true
          };

          const varRequestHourly = {
            ...request,
            weatherDailyVisible: 'ALL',
            nfdrsDailyVisible:'ALL', 
            weatherHourlyVisible: 'ALL', 
            nfdrsHourlyVisible: 'TRUE', 
            weatherDailyDownload: 'ALL', 
            weatherHourlyDownload: 'ALL', 
            nfdrsDailyDownload: 'ALL', 
            nfdrsHourlyDownload: 'ALL', 
            produceNfdrs: 'ALL', 
            ingestObservation: 'ALL', 
            returnAll: true
          };
          if(state.selectedRadio.type==='daily'){
            const responseStations: any = await getStationsMap(varRequest, 'dailyNfdr', state.user);
            setNfdrsErrorsCount(0);
            response = combineStationObservationResponses(responseStations?.stations, responseStations?.obs, 'dailyNfdr', request.endDate, request.fuelModel);
            geoLayers = formatNfdrDataToLayer(response, request.element,request.zoomLevel, request.startDateTime, request.endDateTime, request.radio, request.fuelModel);
            if(responseStations?.stations?.length<1){
              setNfdrsErrorsCount(counter+1)
            }
          }
          if(state.selectedRadio.type==='hourly'){
            const responseStations: any = await getStationsMap(varRequestHourly, 'hourlyNfdr', state.user);
            setNfdrsErrorsCount(0);
            response = combineStationObservationResponses(responseStations?.stations, responseStations?.obs, 'hourlyNfdr', request.endDate, request.fuelModel);
            geoLayers = formatNfdrDataToLayer(response, request.element,request.zoomLevel, request.startDateTime, request.endDateTime, request.radio, request.fuelModel);
            if(responseStations?.stations?.length<1){
              setNfdrsErrorsCount(counter+1)
            }
          }
          
        setResponses(response);
        if (geoLayers) {
          const currObsLayer: any = geoLayers.obsQA_NFDR;
          const currForeLayer: any = geoLayers.forecastsQA_NFDR;
          const currMissing: any = geoLayers.NFDR_Missing;
          const foreMissing: any = geoLayers.Forecast_Missing_NFDR;
          const currObsNOTQA: any = geoLayers.obsNotQA_NFDR;
          const currForecastNOTQA: any = geoLayers.forecastNotQA_NFDR;

          const obsIDNotToRemove = currObsLayer?.id;
          const forecastIDNotToRemove = currForeLayer?.id;
          const missingIDNotToRemove = currMissing?.id;
          const missingForecastIDNotToRemove = foreMissing?.id;
          const obsNotQAIDNotToRemove = currObsNOTQA?.id;
          const forecastNotQAIDNotToRemove = currForecastNOTQA?.id;

          let layerToRemove: any[] = [];
          let matchingLayersObs: any[] = [];
          let matchingLayerForecast: any[] = [];
          let matchingLayerMissing: any[] = [];
          let matchingLayerMissingForecast: any[] = [];
          let matchingLayerNotQAObs: any[] = [];
          let matchingLayerNotQAF: any[] = [];

          map.current.addMany([currMissing,foreMissing, currObsLayer, currForeLayer, currObsNOTQA, currForecastNOTQA])
          map.current.layers.forEach((layer) => {
            if (layer.id.includes('observation') || layer.id.includes('forecastValues') || layer.id.includes('windDirectionArrows') || layer.id.includes('windSpeedBarbs') || layer.id.includes('fieldSample')) {
              layerToRemove.push(layer)
            }
            if (layer.id.includes('obsQA_NFDR') && layer.id !== obsIDNotToRemove) {
              layerToRemove.push(layer)
            }
            if (layer.id.includes('forecastsQA_NFDR') && layer.id !== forecastIDNotToRemove) {
              layerToRemove.push(layer)
            }
            if (layer.id.includes('NFDR_Missing') && layer.id !== missingIDNotToRemove) {
              layerToRemove.push(layer)
            }
            if (layer.id.includes('Forecast_Missing_NFDR') && layer.id !== missingForecastIDNotToRemove) {
              layerToRemove.push(layer)
            }
            if (layer.id.includes('obsNotQA_NFDR') && layer.id !== obsNotQAIDNotToRemove) {
              layerToRemove.push(layer)
            }
            if (layer.id.includes('forecastNotQA_NFDR') && layer.id !== forecastNotQAIDNotToRemove) {
              layerToRemove.push(layer)
            }

            if (layer.id === obsIDNotToRemove) {
              matchingLayersObs.push(layer)
            }
            if (layer.id === forecastIDNotToRemove) {
              matchingLayerForecast.push(layer)
            }
            if (layer.id === missingIDNotToRemove) {
              matchingLayerMissing.push(layer)
            }
            if (layer.id === missingForecastIDNotToRemove) {
              matchingLayerMissingForecast.push(layer)
            }
            if (layer.id === obsNotQAIDNotToRemove) {
              matchingLayerNotQAObs.push(layer)
            }
            if (layer.id === forecastNotQAIDNotToRemove) {
              matchingLayerNotQAF.push(layer)
            }

          });
          if (layerToRemove.length >= 1) {
            map.current.removeMany(layerToRemove)
          }
          if (matchingLayerForecast.length > 1) {
            matchingLayerForecast.pop();
            map.current.removeMany(matchingLayerForecast)
          }
          if (matchingLayersObs.length > 1) {
            matchingLayersObs.pop();
            map.current.removeMany(matchingLayersObs)
          }
          if (matchingLayerMissing.length > 1) {
            matchingLayerMissing.pop();
            map.current.removeMany(matchingLayerMissing)
          }
          if (matchingLayerMissingForecast.length > 1) {
            matchingLayerMissingForecast.pop();
            map.current.removeMany(matchingLayerMissingForecast)
          }
          if (matchingLayerNotQAObs.length > 1) {
            matchingLayerNotQAObs.pop();
            map.current.removeMany(matchingLayerNotQAObs)
          }
          if (matchingLayerNotQAF.length > 1) {
            matchingLayerNotQAF.pop();
            map.current.removeMany(matchingLayerNotQAF)
          }
          return () => {
            if (mapView.current) {
              mapView.current.destroy()
            }
          }
          
        }
          
      }
      catch(error){
          errorResponse(error, addAlert, alertMessages.retrievingStationData)
      }
  })();
  }, [addAlert, map, state.selectedRadio.type, state?.user]);

  /**
   * method to update message to user if there has been a data outage
   */
  const dataOutageBanner = useCallback((data:any)=>{
    data.forEach((n:any)=>{
      if(n.msg_token?.includes('GT50') && !state.GT50Displayed){
  
        setGT50Displayed(true);
          addAlert({
            id:'',
            message: alertMessages.greaterThan50,
            type:'error-info',
            icon:'fa-exclamation-triangle',
            clear: 'persist'
          })
  
      }
      else if(n.msg_token?.includes('GT05') && !state.GT05Displayed){
        setGT05Displayed(true);
        addAlert({
          id:'',
          message: alertMessages.greaterThan05,
          type:'error-info',
          icon:'fa-exclamation-triangle',
          clear: 'persist'
  
        })
      }
      else if(n.msg_token?.includes('SF100') && !state.SF100Displayed){
        setSF100Displayed(true);
        addAlert({
          id:'',
          message: alertMessages.sf100,
          type:'error-info',
          icon:'fa-exclamation-triangle',
          clear: 'persist'
  
        })
      }
      else if(n.msg_token?.includes('NOAA100') && !state.NOAA100Displayed){
        setNOAA100Displayed(true);
        addAlert({
          id:'',
          message: alertMessages.noaa100,
          type:'error-info',
          icon:'fa-exclamation-triangle',
          clear: 'persist'
  
        })
      }
    })
  },[addAlert, state.GT05Displayed, state.GT50Displayed, state.NOAA100Displayed, state.SF100Displayed,
    setGT05Displayed, setGT50Displayed, setNOAA100Displayed, setSF100Displayed
  ]);

  /* outageCallback is to make a call to see if data is missing*/
  const outageCallback = useCallback(()=>{
    (async()=>{
        try{
            const response:any = await checkBannerOutage(addAlert);
            dataOutageBanner(response);
        }
        catch(error){
            errorResponse(error, addAlert, alertMessages.retrievingStationData)
        }
    })();
  },[addAlert, dataOutageBanner]);
  
  /*getHighlightSymbol will be used for hover on map*/
  const getHighlightSymbol = (layerId: string)=>{
    let symbol: any;
    if(layerId.includes('observation') || layerId.includes('obsQA_NFDR')||layerId.includes('NFDR_Missing') || layerId.includes('fieldSample')){
      symbol={
        type: 'simple-marker',
        style: 'circle',
        color:[255, 222, 33, 0.75],
        size:'30px',
        outline:{
          color: [255, 222, 33],
          width: 2
        }
      }
    }
    if(layerId.includes('forecast') || layerId.includes('forecastsQA_NFDR') || layerId.includes('Forecast')){
      symbol={
        type: 'simple-marker',
        style: 'diamond',
        color:[255, 222, 33, 0.75],
        size:'30px',
        outline:{
          color: [255, 222, 33],
          width: 2
        }
      }
    }
    if(layerId.includes('obsNotQA_NFDR')){
      symbol={
        type: 'simple-marker',
        style: 'square',
        color:[255, 222, 33, 0.75],
        size:'30px',
        outline:{
            color: [255, 222, 33],
            width: 2
        }
      }
  }
  if(layerId.includes('forecastNotQA_NFDR')){
      symbol={
        type: 'simple-marker',
        style: 'triangle',
        color:[255, 222, 33, 0.75],
        size:'35px',
        outline:{
            color: [255, 222, 33],
            width: 2
        }
      }
  }
    return symbol
  }
  
  /*below is for the zoom changes*/
  const setCurrentZoom = useCurrentZoom(dispatch);

  /*setTodays... are used to check to see ifso the slider and calls for observations are updated*/
  useEffect(()=>{
    const ref = setInterval(() => {
      setTodaysHour(new Date().getHours());
      setTodaysDay(new Date().getDate());
      setTodaysMonth(new Date().getMonth());
      setTodaysMin(new Date().getMinutes())
    }, 1000);
    return () => {
      if(ref){
        clearInterval(ref);
      }
    }
  },[todaysHour, state.currentDate, todaysDay, todaysMonth]);

  /* usememo is for the 30 min update call--if obs hour has changed then this will update call to weatherobs*/
  useMemo(()=>{
    if(todaysMonth !== new Date(state.currentDate).getMonth() 
      || (todaysHour!== new Date(state.currentDate).getHours() && todaysDay===new Date(state.currentDate).getDate())
      || (todaysDay!== new Date(state.currentDate).getDate())
    ){
      if(new Date(state.currentSliderDate).getTime() ===new Date(state.currentDate).getTime()){
        setTimeHit30(false);
        let today = new Date();
        let myToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), today.getHours(), 0, 0);
        setMiddleDate(myToday.getTime());
        setCurrentSliderDate(myToday.getTime())
        setCurrentDay(myToday.getTime() );
        let past = (myToday.getTime()- (30*ONEMIN));
        let future = (myToday.getTime() + (30*ONEMIN)-ONESEC);    
        setDates([new Date(past), new Date(future)]);
      } 
    }
    if(todaysMin>= 30 && !timeHit30){
      setTimeHit30(true);
      setMiddleDate(state.currentDate+ONEHR);
      setDates([new Date(state.currentDate+ (30*ONEMIN)), new Date(state.currentDate+ (ONEHR+(30*ONEMIN))-ONESEC)])
      setCurrentSliderDate(state.currentDate+ONEHR);
    }

  },[state.currentSliderDate, state.currentDate, setCurrentDay, todaysHour,
    setMiddleDate, setDates, setCurrentSliderDate, todaysMonth,todaysDay,
    todaysMin, timeHit30
  ]);


  /* add map and toolbar to the esri map*/
  useEffect(()=>{
        /*below is to display the map*/
      map.current= new Map({ basemap: 'topo'})
      mapView.current = new MapView({
        map: map.current, 
        zoom: 3.75, 
        center: [-110, 50], 
        container: mapDiv.current,
        popup: {
          defaultPopupTemplateEnabled: false,
          visible: false,
          autoCloseEnabled: false,
        }
      });
    
    /*below is to add the zoom buttons*/
    mapView.current.ui.move('zoom', { position: 'bottom-right' });
    return()=>{
      if(mapView.current){
        mapView.current.destroy()
      }
    }

  }, []);
  
  const settingZoom=useCallback((newZoom: number, returning:boolean)=>{
    if(state.mapDataType==='FieldSample'){
      if (newZoom < 4.25) {
        setCurrentZoom(3);
        if(returning){
          return 3
        }
      }
      if (newZoom >= 4.25) {
        setCurrentZoom(5);
        if(returning){
          return 5
        }
      }
    }
    else{
      if (newZoom < 5) {
        setCurrentZoom(3);
        if(returning){
          return 3
        }
      }
      if (newZoom >= 5 && newZoom < 7) {
        setCurrentZoom(4);
        if(returning){
          return 4
        }
      }
      if (newZoom >= 7) {
        setCurrentZoom(5);
        if(returning){
          return 5
        }
      }
    }
  },[setCurrentZoom, state.mapDataType]);

    /*below is change calls according to zoom*/
  useEffect(() => {
    mapView.current.watch("zoom", function (newZoom) {
      settingZoom(newZoom, false)
    });
  }, [settingZoom]);

  useEffect(()=>{
    if(state.mapDataType==='FieldSample'){
      setSelectedStation(null)
    }
    else{
      setSelectedSite(null)
    }
  },[state.mapDataType])

  /*account for the popup so custom popup has the updated ids and names*/
  useEffect(()=>{
    if(mapView.current){
      mapView.current.when(() => {
        reactiveUtils.watch(
          () => mapView.current.popup?.features,
          (features) => {
            if (features.length !== 0) {
              if(state.mapDataType==='FieldSample'){
                setSelectedSite(features[0].attributes)
                setSelectedStation(null)
              }
              else{
                setSelectedStation(features[0].attributes);
                setSelectedSite(null);
              }
              mapView.current.popup.highlightEnabled =true
            }
            else{
              setSelectedSite(null)
              setSelectedStation(null)
            }
            
          }
        ); 
      });
    }

    if(!selectedStation && mapView.current.popup && state.mapDataType==='Weather'){
      mapView.current.popup.highlightEnabled = false;
    }
    if(!selectedSite && mapView.current.popup && state.mapDataType==='FieldSample'){
      mapView.current.popup.highlightEnabled = false;
    }

  },[selectedStation, selectedSite, state.mapDataType]);

  /*below is to change the basemap if it was saved off*/
  useEffect(()=>{
    if(location.search){
      const stringArray = decodeURIComponent(location.search).split("/");
      const basemap =stringArray[4];
      if(basemap!==map.current.basemap.id){
        if(basemap===imageryUSGSBasemap.id){
          map.current.basemap = imageryUSGSBasemap;   
          setBaseMap(imageryUSGSBasemap);
        }
        else{
          map.current.basemap='topo';
          setBaseMap({id:'topo'})
        }
      }  
    }
  },[location.search, setBaseMap, ]);

      /*below method is for the hover of a station layer for layer id of forecast or observation*/
  useEffect(() => {
    if (mapView.current) {
      let highlightGraphic: any = null;
      let idHovered: any = null;

      mapView.current.on('pointer-move', (event: any) => {
        mapView.current.hitTest(event).then((response: any) => {
          const graphicResults = response.results.find((result: any) => result.layer?.id.includes('forecast') || result.layer?.id.includes('observation') || result.layer?.id.includes('NFDR')|| result.layer?.id.includes('fieldSample'))
          if (graphicResults) {
            if (idHovered !== graphicResults.graphic.attributes.id) {
              if (highlightGraphic) {
                mapView.current.graphics.remove(highlightGraphic);
              }
              highlightGraphic = new Graphic({
                geometry: graphicResults.graphic.geometry,
                symbol: getHighlightSymbol(graphicResults.graphic.layer.id)
              });

              const screenPoint = mapView.current.toScreen(new Point({
                longitude:  graphicResults.graphic.geometry.longitude,
                latitude:  graphicResults.graphic.geometry.latitude
              }))

              mapView.current.graphics.add(highlightGraphic);
              if(graphicResults.layer.id.includes('fieldSample')){
                setTooltipContent(
                  `<div class="tooltip-content for-calc">
                    <div><span style="font-size: 14px; text-transform:uppercase;">${graphicResults.graphic.attributes.name}</span></div>
                    <div style="font-size: 12px; text-transform:capitalize;">Last Sample Date</div>
                    <div style="font-size: 12px;text-transform:uppercase;">${graphicResults.graphic.attributes.latestSampleDate===null? '': moment(graphicResults.graphic.attributes.latestSampleDate).tz(state.userTimeZone.id[0]).format('MM/DD/YY')}</div>
                  </div>`
                );
              }
              else{
                setTooltipContent(
                  `<div class="tooltip-content for-calc">
                    <div><span style="font-size: 14px;text-transform:uppercase;">${graphicResults.graphic.attributes.name}</span></div>
                    <div style="font-size: 12px;text-transform:uppercase;">${graphicResults.graphic.attributes.network}</div>
                    <div style="font-size: 12px;text-transform:uppercase;">${graphicResults.graphic.attributes.state}</div>
                    <div style="font-size: 12px;text-transform:uppercase;">${graphicResults.graphic.attributes.id}</div>
                  </div>`
                );
              }
              setTooltipPosition({ y: screenPoint.y, x: screenPoint.x});
              setTooltipVisible(true);
              idHovered = (graphicResults.graphic.attributes.id)

            }

          }
          else {
            setTooltipVisible(false);
            if (highlightGraphic) {
              mapView.current.graphics.remove(highlightGraphic);
              highlightGraphic = null
              idHovered = null

            }
          }
        });
      });
  
    }
  }, [state.userTimeZone.id]);
    
  /* below is if there are any changes with zooms, dates, or element it 
    will make a call to get new data on map.
    This contains interval to make the calls every 5 mins
  
  */
  useEffect(()=>{
    if(map.current && mapView.current){
      if(state.mapDataType==='Weather'){
        if(state.selectedRadio.type==='hourly' && state.selectedElement){
          const requestVars ={
            zoomLevel: settingZoom(mapView.current?.zoom, true), 
            startDate: moment(state.dates[0]).utc().format(),
            endDate: moment(state.dates[1]).utc().format(),
            radio: state.selectedRadio.type,
            element: state.selectedElement        
          }
          weatherCallback(requestVars, wxErrorCount);
          const interval = setInterval(() => {
            weatherCallback(requestVars, wxErrorCount);
            outageCallback();
          }, 300000);
          if(wxErrorCount>0 && wxErrorCount<5){
            weatherCallback(requestVars, wxErrorCount);
          }
          return () => clearInterval(interval);

        }
        if(state.selectedRadio.type==='daily' && state.selectedElement){
          const requestVars={
            zoomLevel:settingZoom(mapView.current?.zoom, true), 
            startDate: moment(state.dates[0]).utc().format('YYYY-MM-DD'),
            endDate: moment(state.dates[1]).utc().format('YYYY-MM-DD'),
            stationIds: null,
            radio: state.selectedRadio.type,
            element: state.selectedElement
          }
          weatherCallback(requestVars, wxErrorCount)
          const interval = setInterval(() => {
            weatherCallback(requestVars, wxErrorCount);
            outageCallback();
          }, 300000);
          if(wxErrorCount>0 && wxErrorCount<5){
            weatherCallback(requestVars, wxErrorCount);
          }
          return () => clearInterval(interval);
        }
      }

      if(state.mapDataType==='FieldSample'){
        const requestVar={
          zoomLevel: settingZoom(mapView.current?.zoom, true),
          fuelSampleMetadataInput: {
            status: 'Submitted',
            startDate: state.fsMapStartDate,
            endDate: state.fsMapEndDate,
            fuelCategory: state.fsMapCategory,
            fuelType: state.fsMapFuel? state.fsMapFuel.fuel_type:null,
            fuelSubCategory: state.fsMapSubCat?.code_key=== 'None'? null: state.fsMapSubCat?.code_key,
            fuelMethod: state.fsMapMethod?.code_key ==='None'? null: state.fsMapMethod?.code_key
          }
        }
        fieldSampleCallBack(requestVar);
        const interval = setInterval(() => {
          fieldSampleCallBack(requestVar);
          outageCallback();
        }, 300000);
        return () => clearInterval(interval);
      }
      if(state.mapDataType==='FireDanger' && state.selectedElement){
        if(state.selectedRadio.type==='hourly' && state.selectedElement){
          const requestVars ={
            stationIds: null,
            fuelModel: state.nfdrFuelModel,
            zoomLevel: settingZoom(mapView.current?.zoom, true), 
            startDateTime: moment(state.dates[0]).utc().format(),
            endDateTime: moment(state.dates[1]).utc().format(),
            startDate: moment(state.dates[1]).utc().format('YYYY-MM-DD'),
            endDate: moment(state.dates[1]).utc().format('YYYY-MM-DD'),
            startHour: moment(state.dates[1]).utc().format('HH'),
            endHour: moment(state.dates[1]).utc().format('HH'),
            radio: state.selectedRadio.type,
            element: state.selectedElement,
            perPage: 300000,
            page: 0,
          }
          nfdrCallback(requestVars, nfdrsErrorsCount);
          const interval = setInterval(() => {
            nfdrCallback(requestVars, nfdrsErrorsCount);
            outageCallback();
          }, 300000);
          if(nfdrsErrorsCount>0 && nfdrsErrorsCount<5){
            nfdrCallback(requestVars, nfdrsErrorsCount);
          }
          return () => clearInterval(interval);
        }
        if(state.selectedRadio.type==='daily' && state.selectedElement){
          const requestVars ={
            stationIds: null,
            fuelModel: state.nfdrFuelModel,
            zoomLevel: settingZoom(mapView.current?.zoom, true),
            startDateTime: moment(state.dates[0]).utc().format(),
            endDateTime: moment(state.dates[1]).utc().format(),
            startDate: moment(state.dates[1]).utc().format('YYYY-MM-DD'),
            endDate: moment(state.dates[1]).utc().format('YYYY-MM-DD'),
            radio: state.selectedRadio.type,
            element: state.selectedElement,
            perPage: 300000,
            page: 0,
          }
          nfdrCallback(requestVars, nfdrsErrorsCount);
          const interval = setInterval(() => {
            nfdrCallback(requestVars, nfdrsErrorsCount);
            outageCallback();
          }, 300000);
          if(nfdrsErrorsCount>0 && nfdrsErrorsCount<5){
            nfdrCallback(requestVars, nfdrsErrorsCount);
          }
          return () => clearInterval(interval);
        }
      }
    }
  },[state.currentZoom, state.dates, weatherCallback, state.selectedRadio, settingZoom,
    state.selectedElement, outageCallback, state.wind_direction,
    wxErrorCount, nfdrsErrorsCount,
    state.mapDataType, fieldSampleCallBack, state.fsMapEndDate, state.fsMapStartDate, 
    state.nfdrFuelModel, nfdrCallback, state.fsMapCategory, state.fsMapFuel, state.fsMapMethod, state.fsMapSubCat
  ]);

  /* UseEffect Below will check if a call has been made, if we have elevation filter it will apply that filter the the call*/
  useEffect(()=>{
    if(map.current && mapView.current && responses){
      const networkString = state.network.map((network)=>network.where).join(' OR ')

      map.current.layers.forEach((layer:any) => {
        if (layer.id.includes('observation') || layer.id.includes('forecastValues')|| layer.id.includes('windDirectionArrows') || layer.id.includes('windSpeedBarbs') || layer.id.includes('NFDR')) {
          const filter = new FeatureFilter({
            where: `(elevation >= ${state.elevationFilterValues[0]} AND elevation <= ${state.elevationFilterValues[1]}) 
              ${ state.seasonalTrend?.length===1 ? `AND ${state.seasonalTrend[0].where}`:''} \
              ${ state.seasonalTrend?.length>1 ? `AND (${state.seasonalTrend[0].where} OR ${state.seasonalTrend[1].where})`:''} 
              ${ state.seasonalTrend?.length<1 ? `AND (has_historic_data!=0 AND has_historic_data!=1)`:''}
              ${state.network.length > 0 && 'AND'} 
              ${state.network.length > 0 && `(${networkString})`}
            `
          });
          layer.definitionExpression = filter.where;
        }
        if(layer.id.includes('fieldSample')){
          const filter = new FeatureFilter({
            where: `elevation >= ${state.elevationFilterValues[0]} AND elevation <= ${state.elevationFilterValues[1]}`
          });
          layer.definitionExpression = filter.where;
        }
      });
    }
  },[state.elevationFilterValues, responses, state.seasonalTrend, state.network]);

/* this is where the data outage calls are made for initial load*/
  useEffect(()=>{
    if(!calledDataOutage){
      setCalledDataOutage(true);
      outageCallback();
    }
  },[outageCallback, setCalledDataOutage, calledDataOutage]);


  /*find a way to make this better*/
  const returnPosY = (y: any, sh: any)=>{
    if(sh<300){
      return (y- (sh*.4))
    }
    else if(sh<400){
      return (y- (sh*.175))
    }
    else if(sh<450){
      return (y- (sh*.135))
    }
    else if(sh<500){
      return (y- (sh*.095))
    }
    else if(sh<650){
      return (y- (sh*.085))
    }
    else if(sh<800){
      return (y -(sh*.05));
    }
    else if(sh<1800){
      return (y -(sh*.03));
    }
    else{
      return (y -(sh*.025));
    }
  }
  return (
    <div >
      {
        !location.pathname.includes('inap') && <ToolBar view={mapView.current} map={map.current}/>
      }
      <CustomLegend/>
      <div ref={mapDiv} style={{height:'90vh', width: '100vw'}}>
        {
          selectedSite && state.mapDataType ==='FieldSample' && 
          <PopUpFieldSample 
            attributes={selectedSite}
            setSelectedSite={setSelectedSite}
            setPopupPosition={setPopupPosition}
            popupPosition={popupPosition}
          />
        }
        {
          selectedStation && state.selectedRadio.type ==='hourly' && (state.mapDataType ==='Weather') &&
            <PopupWxNfdr 
              attributes={selectedStation} 
              setSelectedStation={setSelectedStation} 
              setPopupPosition={setPopupPosition}
              popupPosition={popupPosition}
              elementList={chartTableList}
            />
        }

        {
          selectedStation && state.selectedRadio.type ==='daily' && (state.mapDataType ==='Weather') &&
            <PopupWxNfdr 
              attributes={selectedStation} 
              setSelectedStation={setSelectedStation} 
              setPopupPosition={setPopupPosition}
              popupPosition={popupPosition}
              elementList={chartTableListDaily}
            />
        }

        {
          selectedStation && state.selectedRadio.type ==='daily' && (state.mapDataType ==='FireDanger') &&
            <PopupWxNfdr 
              attributes={selectedStation} 
              setSelectedStation={setSelectedStation} 
              setPopupPosition={setPopupPosition}
              popupPosition={popupPosition}
              elementList={ndrsChartDailyList}
            />
        }


        {
          selectedStation && state.selectedRadio.type ==='hourly' && (state.mapDataType ==='FireDanger') &&
            <PopupWxNfdr 
              attributes={selectedStation} 
              setSelectedStation={setSelectedStation} 
              setPopupPosition={setPopupPosition}
              popupPosition={popupPosition}
              elementList={ndrsChartHourlyList}
            />
        }
        </div>
      {mapView.current && map.current && !location.pathname.includes('inap') && <BaseMapToggleButton view={mapView.current} map={map.current}/>}
      {mapView.current && (state.mapDataType==='Weather'|| state.mapDataType==='FireDanger') && !location.pathname.includes('inap') &&<SearchWeatherStations view={mapView.current}/>}
      {mapView.current && state.mapDataType==='FieldSample' && !location.pathname.includes('inap') &&<SearchSite view={mapView.current}/>}

      {tooltipVisible && (
        <Tooltip
          title={<span dangerouslySetInnerHTML={{ __html: tooltipContent }} />}
          open={tooltipVisible}
          className='for-calc'
          placement="bottom"
          style={{ position: 'absolute', left: tooltipPosition.x, top: returnPosY(tooltipPosition.y, screenHeight), zIndex:500}}
        >
          <span />
        </Tooltip>
      )}
    </div>
  );
}
export default EsriMap;
